define((require) => {
    "use strict";

    const {
        clone, each, isString, pluck
    }         = require("underscore");
    const Browser                                = require("shared/services/Browser");
    const Channel                                = require("ui/publish/channelSetup/Channel");
    const Configuration                          = require("shared/services/Configuration");
    const EditorEvents                           = require("shared/services/EditorEvents");
    const ExperienceModel                        = require("shared/ui/experience/ExperienceModel");
    const GroupCollection                        = require("shared/ui/groups/GroupCollection");
    const BreakpointGroup                        = require("shared/ui/breakpoint/BreakpointGroup");
    const LoggedIn                               = require("shared/security/LoggedIn");
    const {Logger}                                 = require("shared/util/Logger");
    const Marionette                             = require("marionette");
    const MessageNode                            = require("services/messageNode/MessageNode");
    const MessageTypes                           = require("ui/publish/MessageTypes");
    const pageStatus                             = require("ui/publish/PageStatus");
    const Permissions                            = require("shared/security/Permissions");
    const Promise                                = require("shared/util/Promise");
    const ProjectAsset                           = require("ui/editor/project/ProjectAsset");
    const PublishUtil                            = require("ui/publish/PublishUtil");
    const PublishToolbarThumbnailView            = require("ui/publish/PublishToolbarThumbnailView");
    const SequentialButton                       = require("util/SequentialButton");
    const SyncManager                            = require("shared/services/sync/SyncManager");
    const SystemMessages                         = require("editor/services/SystemMessages");
    const template                               = require("ui/publish/PublishToolbarView.tpl");
    const Time                                   = require("shared/services/Time");
    const UrlUtil                                = require("shared/util/UrlUtil");
    const Window                                 = require("shared/services/Window");

    require("shared/ui/breakpoint/BreakpointGroupRangeMixin"); // this is a standalone app that needs this!

    const CHECKING_TIMEOUT = 5000;
    const CURRENT_SCHEDULED_DATE = "now";

    const CONTACT_US_COMMUNITY_URL = `${Configuration.communityBaseUrl}/hc/en-us/articles/202049238-Contact-Us`;

    const log = new Logger("PublishToolbarView");

    let node;
    let changed = false;
    let closed = false;

    function framed() {
        return window.top !== window.self;
    }

    // For now, we support all browsers (and versions) allowed into the editor, but we can change this later on if we
    // experience problems. However, IE, Opera, and Chrome on non Windows OS does not support preview mode either! IE
    // does not support cross domain drag-n-drop between *windows* so we use a an iframe solution there. Setting this
    // flag to false in some context will ensure that a proper state is set/rendered!
    function browserSupported() {
        return !(Browser.type.ie || Browser.type.edge) || framed(); // will be "publish" if two windows are used
    }

    // If Chrome on anything but windows, we cannot do preview since we have no way of displaying the
    // bookmarks in the opened window, i.e. the "Experience Preview" bookmark can never be clicked.
    // For Opera, bookmarks are gone from version 18, but can apparently be imported in certain cases.
    // However, this will make it very hard to make a simple wizard description, so for know, we disable
    // preview in Opera altogether (even though old versions of it may support bookmarks just fine).
    // Bookmarks is gone from Safari as of 9.0 so we disable it completely too.
    function previewNotSupported() { // assumes supported browser!
        // Never possible to use the bookmarklet if iframed, since a bookmarklet can only alter the top document...
        return framed() || Browser.type.ie || Browser.type.edge || Browser.type.opera || (Browser.type.chrome && !Browser.os.winOS) || Browser.type.safari;
    }

    // Closes this window (and the customer site when closing node)...
    function doClose() {
        if (!closed) {
            if (!changed || confirm("You are leaving a page with unsaved changes.\n\nAre you sure you want to leave this page?")) {
                // Tag as already closed as to not alert multiple confirm messages to the user (see beforeNavigation)...
                closed = true;
                // Will relay message to "channels_admin" that is should close it self. This is necessary since if
                // the "publish" window is reloaded, the JS references to this and the "channels_admin" window will
                // be lost, and they will never be closed...
                node.close();
                // Close this window, necessary if connection is lost...
                window.close();
            }
        }

        return true;
    }

    return Marionette.Layout.extend({

        template,

        className: "publish-toolbar-view pt-page",

        transitions: {
            "in": "pt-page-moveFromTop",
            out: "pt-page-scaleDown"
        },

        regions: {
            experienceThumbnails: ".experience-thumbnails",
            publishThumbnailRegion: ".publish-thumbnail-region"
        },

        events: {
            "click #settings-link":     "_settingsClickHandler",
            "click .highlight-toggle":  "_highlightToogleClickHandler",
            "click .save-button":       "_saveClickHandler",
            "click .discard-changes":   "_discardChangesClickHandler",
            "click #support":           "_supportClickHandler",
            "click .close-button":      doClose
        },

        ui: {
            highlightToggleButton:     ".highlight-toggle",
            discardButton:             ".discard-changes",
            statusLabel:               "#status-label",
            statusWrapper:             "#status-wrapper",
            status:                    ".status",
            statusChecking:            ".status.checking",
            statusBrowserNotSupported: ".status.browser-not-supported",
            statusPreviewNotSupported: ".status.preview-not-supported",
            statusWindowClosed:        ".status.window-closed",
            statusNoSnippet:           ".status.no-snippet",
            statusWrongBookmarklet:    ".wrong-bookmarklet",
            statusSnippetOk:           ".status.snippet-ok",
            statusSnippetNotOk:        ".status.snippet-not-ok",
            statusHasChannels:         ".status .has-channels",
            statusMessage:             ".status-message",
            savePreviewButton:         ".save-button"
        },

        templateHelpers() {
            const {project} = this.options;
            return {
                supportedBrowser:    browserSupported(),
                previewSupported:    !previewNotSupported(),
                requiresShowAsTab:   Browser.type.chrome && Browser.os.winOS,
                browser:             Browser,
                notAllowedToPublish: this._notAllowedToPublish,
                project,
                config:              Configuration // only available by default for ImprovedViews
            };
        },

        channelBindings: {
            ".bookmarklet": {attributes: [{name: "href", observe: "bookmarklet"}]}
        },

        _timer: null,
        _highlight: "on",
        _channelDescriptors: null,
        _currentPageStatus: pageStatus.UNKNOWN_IF_ENABLED, // since we set it to CHECKING on render, has to be different
        _snippetPreview: false,
        _notAllowedToPublish: false,

        publishables: null, // publishables with possible state changes...

        _settingsClickHandler(event) {
            event.preventDefault();
            node.post({action: MessageTypes.NAVIGATE_TO, data: {url: "#settings"}, receiver: "root"});
        },

        _highlightToogleClickHandler() {
            if (!this.ui.highlightToggleButton.hasClass("disabled")) {
                this._toggleHighlight();
            }
        },

        _saveClickHandler() {
            this._saveButton();
        },

        _discardChangesClickHandler() {
            if (!this.ui.discardButton.hasClass("disabled")) {
                this._refresh("Changes successfully discarded", "Could not reload Slot configuration, please close the window to discard changes!");
            }
        },

        _supportClickHandler(event) {
            event.preventDefault();
            // Use Zendesk quick view in the editor if possible, i.e. if we can connect to the Editor, otherwise
            // link directly to the community...
            if (node.root() === "publish") { // can be framed, so we have to check for named node!
                node.post({action: MessageTypes.SUPPORT, receiver: "publish"});
            } else {
                Window.open(CONTACT_US_COMMUNITY_URL, "zmags_creator_community");
            }
        },

        /**
         * Updates the UI to visually indicate the status of the site in the "customer site window"
         *
         * @param {pageStatus} status The status of the site (if it is enabled for channels, has any channels, etc.)
         * @private
         */
        _setPageStatus(status) {
            this._clearTimer();

            if (this._currentPageStatus.id === status.id ||
                this._currentPageStatus.id === pageStatus.BROWSER_NOT_SUPPORTED.id ||
                ((this._currentPageStatus.id === pageStatus.WRONG_BOOKMARKLET.id ||
                  this._currentPageStatus.id === pageStatus.PREVIEW_NOT_SUPPORTED.id) && status.id === pageStatus.WINDOW_CLOSED.id)) { // ignore auto-reload!
                return;
            }

            this._currentPageStatus = status;
            this.ui.status.css("display", "none");

            switch (status.id) {
                case pageStatus.CHECKING.id:
                    this.ui.statusChecking.css("display", "block");
                    break;

                case pageStatus.BROWSER_NOT_SUPPORTED.id:
                    this.ui.statusBrowserNotSupported.css("display", "block");
                    node.post(MessageTypes.BROWSER_NOT_SUPPORTED);
                    break;

                case pageStatus.PREVIEW_NOT_SUPPORTED.id:
                    this.ui.statusPreviewNotSupported.css("display", "block");
                    break;

                case pageStatus.WINDOW_CLOSED.id:
                    this.ui.statusWindowClosed.css("display", "block");
                    break;

                case pageStatus.WRONG_BOOKMARKLET.id:
                    node.post(MessageTypes.WRONG_BOOKMARKLET);
                    this.ui.statusNoSnippet.css("display", "block");
                    this.ui.statusWrongBookmarklet.css("display", "block");
                    this._setMessage("Please update your bookmark in your bookmarks/favorites bar!", true);
                    this._snippetPreview = true; // since only when "Experience Preview" is clicked
                    break;

                case pageStatus.WRONG_ID.id:
                    this.ui.statusSnippetNotOk.css("display", "block");
                    node.post({action: MessageTypes.WRONG_CONFIG_ID, data: this.options.channelConfig.get("id")});
                    break;

                case pageStatus.NOT_ENABLED_NO_CHANNELS.id:
                case pageStatus.NOT_ENABLED_HAS_CHANNELS.id:
                    if (previewNotSupported()) {
                        return this._setPageStatus(pageStatus.PREVIEW_NOT_SUPPORTED);
                    }

                    this._snippetPreview = true;
                    this.ui.statusHasChannels.css("display", status.id === pageStatus.NOT_ENABLED_NO_CHANNELS.id ? "none" : "");
                    // Fall through.

                case pageStatus.ENABLED_HAS_CHANNELS.id:
                case pageStatus.ENABLED_NO_CHANNELS.id:
                    this.ui.statusSnippetOk.css("display", "block");
                    this.ui.statusSnippetOk.toggleClass("preview", this._snippetPreview);
                    break;

                default: // UNKNOWN_IF_ENABLED
                    if (previewNotSupported()) {
                        return this._setPageStatus(pageStatus.PREVIEW_NOT_SUPPORTED);
                    }

                    this.ui.statusNoSnippet.css("display", "block");
                    break;
            }
        },

        _setMessage(text, err, fixed, delay) { // TODO: eventually use NotificationBar!
            const {statusMessage} = this.ui;

            statusMessage.css({
                "display": "none",
                "color": typeof err !== "undefined" ? "orange" : "black"
            });
            statusMessage.text(text || "");

            if (err) {
                // Http call?
                if (typeof err.statusText !== "undefined") {
                    log.error(`${text} => ${err.statusText} (${err.status}) -> ${err.responseText}`, err);
                // Some other error, for example a received error from the client site...
                } else {
                    log.error(`${text} => ${(err.message || err)}`, err);
                }
            } else if (text) {
                log.info(text);
            }

            if (text) {
                if (fixed) {
                    statusMessage.fadeIn("fast");
                } else {
                    if (!delay) {
                        if (err) {
                            delay = 6000;
                        } else {
                            delay = 3000;
                        }
                    }

                    statusMessage.fadeIn("fast").delay(delay).fadeOut("fast");
                }

                statusMessage.one("click", () => {
                    statusMessage.css("display", "none");
                });
            }
        },

        _startTimer() {
            // If the "toolbar" window is loaded within this interval, it will send a PAGE_IS_CHANNELS_ENABLED
            // status ping....
            if (browserSupported()) {
                this._timer = setTimeout(() => {
                    this._setPageStatus(pageStatus.UNKNOWN_IF_ENABLED);
                }, CHECKING_TIMEOUT);
            } else {
                this._setPageStatus(pageStatus.BROWSER_NOT_SUPPORTED);
            }
        },

        _clearTimer() {
            if (this._timer) {
                clearTimeout(this._timer);
            }

            this._timer = null;
        },

        /**
         * @private
         * Returns a JSON version of the channel config, that can be sent using "postMessage". <p>
         *
         * Augments/changes the channel json by: <ol>
         *
         * <li> Each channel will have an (temp) id if it does not already have an id. <p>
         *
         * <li> Adding the "inProject" flag for any channel, experience, and/or group in question when in
         *      project mode (true/false), always false otherwise (i.e. also when not in project mode).
         *
         * <li> If a schedule is present, it will be stored under <tt>channel.schedule</tt> (and not in
         *      an array with a single entry).
         *
         * </ol>
         *
         * @return {*} A postable channel config with augmented channels; never falsey.
         */
        _getPostableChannelConfig() {
            const {channelConfig, project} = this.options;
            // The toJSON may still share objects, like schedules, that will eventually be stringified to json at
            // this point, so we cannot update the blindly here as it will be reflected in the channel config!
            const json = channelConfig.toJSON({sync: "marshal"});

            // Marshals the publishable associated with the specified schedule, "Now" (channel) or
            // actual schedule...
            const publishable = (schedule) => {
                if (schedule.experience) {
                    schedule.experience = this._experienceToJson(project, schedule.experience);
                    delete schedule.group;
                } else if (schedule.group) {
                    schedule.group = this._groupToJson(project, schedule.group);
                    delete schedule.experience;
                } else {
                    delete schedule.experience;
                    delete schedule.group;
                }
            };

            json.channels = json.channels.map((channel) => clone(channel));

            json.channels.forEach((channel) => {
                if (project) {
                    channel.inProject = project.containsChannel(channel.id || channel.cid); // handles unsaved ones as well
                }

                // For newly created channels, no "urlPattern" has been set by the backend, so we must set it here...
                if (!channel.urlPattern && channel.url) {
                    channel.urlPattern = channel.url.split("*");
                }

                // Schedule "Now" (1)...
                publishable(channel);

                // Future Schedule (2)...
                if (channel.schedules) {
                    // "schedule" is a shared reference, we must copy it!
                    let schedule = channel.schedules[0];
                    delete channel.schedules; // not a shared reference

                    if (schedule) {
                        schedule = clone(schedule);
                        publishable(schedule);
                        channel.schedule = schedule;
                    }
                }
            });

            return {
                channelConfig: json,
                origin:        node.origin()
            };
        },

        /**
         * @param  {String|Model|Object} publishable
         * @return {[type]}             [description]
         */
        _lookupPublishable(publishable) {
            const id = isString(publishable) ? publishable : publishable.id || publishable.get("id");

            return this.options.publishableCollection.get(id);
        },

        _experienceToJson(project, experience) {
            if (experience && experience.__typeName) { // we know we have a typeName on our Experience backbone models...
                experience = {
                    id:       experience.get("id"),
                    version:  Math.abs(experience.get("version")),
                    name:     experience.get("name"),
                    state:    experience.get("state")
                };
            }

            // We can never create new experiences here, so we do not have to test for temp id...
            if (project) {
                experience.inProject = project.containsExperience(experience.id);
            }

            return experience;
        },

        _groupToJson(project, group) {
            if (group) {
                if (group.__typeName) { // we know we have a typeName on our Group backbone models...
                    group = group.toJSON();
                }

                group = {
                    // Only groups of type "BreakpointGroup" can be published - but still we need to
                    // tell the backend (thanks Mr. ALA).
                    type:    BreakpointGroup.type,
                    id:      group.id,
                    // version: Math.abs(group.version),
                    name:    group.name,
                    state:   group.state,
                    info:    group.name
                };
            }

            // We can never create new groups here, so we do not have to test for temp id...
            if (project) {
                group.inProject = project.containsGroup(group.id);
            }

            return group;
        },

        _getHighlight() {
            return this._highlight;
        },

        /**
         * @private
         *
         * Post a message to the customer site instructing it to update all the channels according to the given channelConfig.
         *
         * @param {Boolean} [updated] If true, we notify the "publish" node as well, so it can reload the new data itself.
         */
        _postUpdateChannelsMessage(updated) {
            this._setMessage(); // clear any message present now...
            let receivers = ["channels_admin"];

            if (updated) {
                receivers.push("root");
            }

            changed = !updated && !this._notAllowedToPublish; // potentially changed unless updated, we really do not know...

            if (changed) {
                this._setMessage("Unsaved changes!", false, true);
            }

            this._updateChangedButtons(changed);

            const data = this._getPostableChannelConfig();
            // Add scheduled date...
            data.scheduledDate = this._scheduledDate;
            node.post({action: MessageTypes.POPULATE_CHANNELS, receiver: receivers, data: data});
        },

        _updateChangedButtons(changed) {
            const {discardButton} = this.ui;
            this._saveButton.enable(changed && !this._notAllowedToPublish);

            if (this._notAllowedToPublish || !changed) {
                discardButton.addClass("disabled");
            } else {
                discardButton.removeClass("disabled");
            }
        },

        /**
         * @private
         * Handles a message (from the customer site window) to resolve content information for in-place channels
         * where only an Experience and/or group id is available.
         *
         * @param {MessageNode.Message} message The received message; never null.
         */
        _resolveContent(message) {
            const data = message.data;
            const type = data.type;
            const id = data.id;
            const silent = {silent: true}; // do not alert such errors here in the Publish toolbar, the error will be shown in tooltips on the client site...
            const promise = type === "experience" ? ExperienceModel.findById(id, silent) : GroupCollection.fetchOne(id, silent);
            const {project} = this.options;

            promise.done((model) => {
                node.reply(message, {channel: data.channel, content: type === "experience" ? this._experienceToJson(project, model) : this._groupToJson(project, model)});
            }).fail((xhr) => {
                log.error("Could not resolve content for 'in-place' channel: " + message, message, xhr.status);
            });
        },

        /**
         * @private
         * Handles a message (from the customer site window) indicating that an content should be published to the
         * given channel.
         *
         * @param {MessageNode.Message} message The received message; never null.
         */
        _publishToChannel(message) {
            const channel = this._getChannelByMessageChannelId(message);

            if (channel) {
                // "No Experience" scheduled for Schedule 2?
                if (message.data.noContent) {
                    this._publishContentToChannel(channel); // no content, so "type" does not matter
                    this._postUpdateChannelsMessage();

                    return;
                }

                // Find/load actual model and add it to the channel...
                this._addModelToChannel(channel, message.data.id, message.data.type);
            }
        },

        /**
         * @private
         * Fetches the specified model based on its id and type (name), then adds it to the supplied channel.
         *
         * @param {Channel} channel The channel; never falsey.
         * @param {String}  id      The model id, e.g. an experience id; never falsey.
         * @param {String}  type    The model type, e.g. "experience" or "group"; never falsey.
         *
         * @return {Promise} A promise that resolves once the model has been loaded and added to the channel and
         *                   an update of the resulting channel config has been sent to the channels admin app.
         */
        _addModelToChannel(channel, id, type) {
            const promise = type === "experience" ? ExperienceModel.findById(id) : GroupCollection.fetchOne(id);

            return promise.then((model) => {
                this._publishContentToChannel(channel, model, type);
                this._postUpdateChannelsMessage();
            });
        },

        /**
         * @private
         * Will publish a content Model based on the scheduled date. If the scheduled date is "now" or undefined
         * we assume that the current content is the one changed. Otherwise the scheduled content is the one changed. <p>
         *
         * This will also tag <tt>channel</tt> as edited "now" and as modified via DnD.
         *
         * @param {Channel}  channel  The channel to publish to
         * @param {Model}   [content] The content model to publish (ExperienceModel or GroupModel); will be null if
         *                            "No Experience" is scheduled in Schedule 2.
         * @param {String}  [type]    The type of content; can be falsey if <tt>content</tt> is null.
         */
        _publishContentToChannel(channel, content, type) {
            if (!this._scheduledDate || this._scheduledDate === CURRENT_SCHEDULED_DATE) {
                if (type === "experience") {
                    Channel.publishCurrentExperience(channel, content);
                } else {
                    Channel.publishCurrentGroup(channel, content);
                }
            } else if (type === "experience") {
                Channel.publishScheduledExperience(channel, content, this._scheduledDate);
            } else {
                Channel.publishScheduledGroup(channel, content, this._scheduledDate);
            }

            // Tag channel as edited at, but do it silently - nobody is listening for these meta data anyways...
            // channel.set({editedAt: Time.server(), editedBy: LoggedIn.principal().get("userId"), dnd: true}, {silent: true});
            channel.editedAt = Time.server();
            channel.editedBy = LoggedIn.principal().get("userId");
            channel.dnd = true;

            const {project} = this.options;
            // Make sure we add the channel as an asset if not already and the experience is from the current
            // project (we never save the project from here). This is regardless if the experience in question is
            // part of the project or not, since it will be inferred on save. We thus only need to concern
            // ourselves with the channel for the client site to get the layout right...
            if (project) {
                const id = channel.id || channel.cid;
                const assets = project.get("assets");
                const inProject = assets.containsChannel(id);

                if (!inProject) {
                    assets.addResource(id, ProjectAsset.Type.CHANNEL);
                }
            }

            const hasContent = Channel.hasContent(channel);
            let eventName = EditorEvents.constants.ADD_CHANNEL;

            if (hasContent) {
                eventName = EditorEvents.constants.UPDATE_CHANNEL;
            }

            const eventProperties = PublishUtil.buildEventProperties({
                channel: channel,
                triggerOrigin: "ClientWindowContainer",
                dragAndDrop: true,
                manual: false,
                projectId: project && project.get("id"),
                updatedProperty: hasContent ? type : null,
                updatedValue: hasContent ? content.get("id") : null
            });

            EditorEvents.trigger(eventName, eventProperties);
        },

        /**
         * @private
         * Handles a message (from the customer site window) indicating that a new channel should be created and the
         * given experience should be published to it. Also deletes any existing channels that happen to conflict
         * with the new channel that will be created (channels cannot be nested inside other channels). <p>
         *
         * This will also tag the created channel as created "now" via DnD.
         *
         * @param {MessageNode.Message} message The received message; never null.
         */
        _createChannelAndPublish(message) {
            const {channelConfig, project} = this.options;

            const newChannel = new Channel({ // not a snippet channel, but an editor model
                name:      message.data.name,
                url:       message.data.url,
                selector:  message.data.selector,
                createdBy: LoggedIn.principal().get("userId"),
                createdAt: Time.server(),
                dnd:       true // created via DnD!
            });

            const cid = newChannel.cid;
            const channelJson = newChannel.toJSON();
            const channels = channelConfig.get("channels");
            const assets   = project && project.get("assets");

            channelJson.cid = cid;

            channels.push(channelJson);

            // Since we are in "unsaved changes mode" here, we need to add a project reference for the channel if
            // in project mode. After save, the assets will have been added and get their actual ids...
            if (assets) {
                assets.addResource(cid, ProjectAsset.Type.CHANNEL);
            }

            // Selector of the channel that the new channel replaces (if any)
            each(message.data.remove, (id) => {
                // Invariant: "id" is either an actual id or a temp "cid", Backbone collections support both...
                const channelJson = channelConfig.channelById(id);

                if (channelJson) {
                    // Remove the channel as an asset as it will be destroyed/deleted when (if) the config is saved.
                    // Since channels are not soft-deleted, we do not bother tagging the channel with who did this...
                    if (assets) {
                        assets.removeResource(channelJson.id, ProjectAsset.Type.CHANNEL);
                    }

                    log.info("Removing channel because of new channel containing it: " + channelJson + " (new) >> " + channelJson + " (will be deleted on save)", channelJson, channelJson);

                    channelConfig.removeChannel(channelJson);
                }
            });

            this._addModelToChannel(channelJson, message.data.id, message.data.type);
        },

        _getChannelByMessageChannelId(message) {
            return this.options.channelConfig.channelById(message.data.channel);
        },

        /**
         * @private
         * Handles a message (from the customer site window) indicating that the channel content should be cleared. <p>
         *
         * This will also tag the channel as edited "now" and as modified via DnD.
         *
         * @param {MessageNode.Message} message The received message; never null.
         */
        _removeContent(message) {
            const channelJson = this._getChannelByMessageChannelId(message);

            if (channelJson) {
                // Tag channel as edited at, but do it silently - nobody is listening for these meta data anyways...
                channelJson.editedAt = Time.server();
                channelJson.editedBy = LoggedIn.principal().get("userId");
                channelJson.dnd = true;

                if (message.action === MessageTypes.REMOVE_SCHEDULED_CONTENT) {
                    Channel.publishScheduledExperience(channelJson, null); // clears all schedules
                } else {
                    Channel.publishCurrentExperience(channelJson, null); // also clears current group since an experience is set
                }

                const eventProperties = PublishUtil.buildEventProperties({
                    channel: channelJson,
                    triggerOrigin: "RemoveContentButton",
                    dragAndDrop: true,
                    manual: false,
                    projectId: this.project && this.project.get("id")
                });

                EditorEvents.trigger(EditorEvents.constants.UPDATE_CHANNEL, eventProperties);
            }

            this._postUpdateChannelsMessage();
        },

        /**
         * @private
         * Handles a message (from the customer site window) indicating that the given channel should be deleted.
         *
         * @param {MessageNode.Message} message The received message; never null.
         */
        _deleteChannel(message) {
            const channelJson = this._getChannelByMessageChannelId(message);

            // Since channels are not soft-deleted, we do not bother tagging the channel with who did this...
            if (channelJson) {
                Channel.publishCurrentExperience(channelJson, null);   // also clears current group since an experience is set
                Channel.publishScheduledExperience(channelJson, null); // clears all schedules

                const {channelConfig} = this.options;

                channelConfig.removeChannel(channelJson);

                // Might be a "temp" channel (cid)
                if (channelJson.id) {
                    // We are in "unsaved country", so we have to manually remove the channel as an asset if and only
                    // if it already was part of the project...
                    if (this.project) {
                        this.project.get("assets").removeResource(channelJson.id, ProjectAsset.Type.CHANNEL);
                    }

                    const eventProperties = PublishUtil.buildEventProperties({
                        channel: channelJson,
                        triggerOrigin: "DeleteChannelButton",
                        dragAndDrop: true,
                        manual: false,
                        projectId: this.project && this.project.get("id")
                    });

                    EditorEvents.trigger(EditorEvents.constants.DELETE_CHANNEL, eventProperties);
                }
            }

            this._postUpdateChannelsMessage();
        },

        /**
         * @private
         * Handles a message (from the customer site window) indicating that the selected scheduled date has
         * changed and/or if that changed requires us to post a new channel config for a full update because
         * at least one channel will change it contents because of it.
         *
         * @param {MessageNode.Message} message The received message; never null.
         */
        _scheduledDateChanged(message) {
            // If we have a correlation id, "message" is a response from the channel admin app whether or not
            // the original schedule date cause content to be changed on the page...
            if (message.correlationId) {
                // True: update, false: no update required, but we are in sync...
                if (message.data === "update") {
                    this._postUpdateChannelsMessage(false); // false: no update, only selected date change
                }
                return;
            }

            const newScheduledDate = message.data === CURRENT_SCHEDULED_DATE ? message.data : parseInt(message.data, 10);

            if (this._scheduledDate !== newScheduledDate) {
                this._scheduledDate = newScheduledDate;
                // A REPLY selector to hit this callback again with a response from the channels admin app whether
                // we should post a full update or not, which is what we are testing for above with the correlation
                // id...
                node.post({action: MessageTypes.SCHEDULED_DATE_CHANGED, data: newScheduledDate, selectors: MessageNode.Message.Selectors.REPLY});
            }
        },

        /**
         * @private
         * Sends a message to the customer site window telling it to toggle highlighting of the channels, and updates the
         * highlight button to match the new state. <p>
         *
         * If the highlight is currently disabled, it will continue to be so until the client site tells otherwise or
         * we get a manual update from the editor.
         *
         * @param {String} [highlight] A specific highlight state to enable; default toggles based on current state.
         */
        _toggleHighlight(highlight) {
            // "restore" means restoring old highlight from the client side. This is done be passing on "restore" and
            // the client side responding with the previous value, eventually hitting this function again with the
            // actual value...
            if (highlight === "restore") {
                node.post({action: MessageTypes.SET_HIGHLIGHT, data: "restore", selectors: MessageNode.Message.Selectors.REPLY});
                return;
            }

            const old = this._highlight;

            if (highlight) {
                this._highlight = highlight;
            } else if (this._highlight === "on") {
                this._highlight = "off";
            } else if (this._highlight === "off") {
                this._highlight = "on";
            } else {
                this._highlight = "disabled";
            }

            node.post({action: MessageTypes.SET_HIGHLIGHT, data: this._highlight});

            let text = "Remove Highlight";
            if (this._highlight === "off") {
                text = "Highlight";
            } else if (this._highlight === "disabled") {
                text = "Highlight";
                if (old === "on") {
                    text += " Hidden";
                }
            }

            const {highlightToggleButton} = this.ui;
            highlightToggleButton.text(text);

            if (this._highlight === "disabled") {
                highlightToggleButton.addClass("disabled");
            } else {
                highlightToggleButton.removeClass("disabled");
            }
        },

        initialize() {
            this._notAllowedToPublish = Permissions.Privileges.PUBLISH.cannot();

            // Log config; ignore ping about channels being enabled, or it will flood the log...
            const logOptions = {log: Logger.level() >= Logger.LOG, ignore: MessageTypes.PAGE_IS_CHANNELS_ENABLED};

            // Create message node to (mainly) communicate with the "channels_admin" node at "this.options.url", i.e.
            // the page/iframe injected via the bookmarklet on the client website. As for the "Publish" tab, we
            // do not know if the specified url will redirect once loaded, e.g. "dr.dk" means "www.dr.dk", so we again
            // have to match on the origin ending with the hostname...
            node = new MessageNode({
                name: "toolbar", eksport: "__zmags.creator.node", log: logOptions, defaults: "channels_admin", origins: {allowed: UrlUtil.location(this.options.url, "hostname"), callback: "err"}, ignorePostAfterClosed: true
            });

            // Post to "channels_admin" node that it should initialize it self. It does not matter if said node is up
            // yet, messages will be enqueued for it and dispatched when it becomes ready. However, if "channels_admin"
            // leaves and re-joins, e.g. a reload, it must be re-initialized. This is handled below in the "on.leave"
            // and "on.join" actions...
            const flags = {notAllowedToPublish: this._notAllowedToPublish};
            const {project, channelConfig} = this.options;

            if (project) {
                flags.projectId = project.get("id");
            }

            const handshake = node.bind([
                // Let the ChannelAdminApplication know whether the user is allowed to actually publish things or not
                {
                    action: MessageTypes.SET_FLAGS,
                    data: flags,
                    selectors: MessageNode.Message.Selectors.REPLY
                },
                {
                    action: MessageTypes.INITIALIZE_CHANNELS,
                    data: this._getPostableChannelConfig.bind(this),
                    selectors: MessageNode.Message.Selectors.REPLY
                },
                {
                    action: MessageTypes.POPULATE_CHANNELS
                },
                {
                    action: MessageTypes.SET_HIGHLIGHT,
                    data: this._getHighlight.bind(this)
                }
            ]);

            let initialized = null;
            const on = {};

            // Use highlight response from SET_FLAGS in case this node is doing a reload...
            on[MessageTypes.SET_FLAGS] = (message) => {
                if (this._highlight !== message.data) {
                    this._toggleHighlight(message.data);
                }
            };

            // If the channels admin application discovers that the browser is not supported, e.g. IE 10
            // in protected mode...
            on[MessageTypes.BROWSER_NOT_SUPPORTED] = () => {
                this._setPageStatus(pageStatus.BROWSER_NOT_SUPPORTED);
            };

            // Notify user of errors from the client page...
            on[MessageNode.Message.Actions.ERROR] = (message) => {
                // If the drop failed, it is very likely due to the deduced Sizzle selector being invalid...
                if (message.data) {
                    let msg;

                    if (message.data.action === MessageTypes.DROP) {
                        msg = "The selected element cannot be used, please select another one!";
                    } else if (message.data.action === MessageTypes.DRAG_START) {
                        msg = message.data.message || message.data.error;
                    }

                    if (!msg) {
                        msg = "Sorry, but something went wrong, please try again!";
                    }

                    this._setMessage(msg, message.data, true);
                }
            };

            // Keep tabs of "publish" and "channels_admin" nodes...
            on[MessageNode.Message.Actions.LEAVE] = (message) => {
                if (message.origin === "channels_admin") {
                    initialized = false;
                    // We do not know the current schedule date any longer, i.e. must use the first incoming one
                    // on re-connect...
                    this._scheduledDate = null;
                } else if (node.ancestor(message.origin) && message.data !== "self") { // TODO: was === "force" - how do we catch user closing browser window from a reload?
                    window.close();
                }

                this._setPageStatus(pageStatus.WINDOW_CLOSED); // some window!
            };

            on[MessageNode.Message.Actions.JOIN] = (message) => {
                if (message.origin === "channels_admin") {
                    if (initialized === false) {
                        initialized = true;
                        handshake();
                    }
                }
            };

            on[MessageTypes.INITIALIZE_CHANNELS] = (message) => {
                // Relay to root, so we can cancel timer check for client site breaking out of the frame. We do this
                // always, never mind if we are loaded at the wrong domain...
                node.post({action: MessageTypes.INITIALIZE_CHANNELS, receiver: "root"});
                initialized = true;

                const remoteOrigin = message.data;
                const origin = node.origin(); // only from "channels_admin"

                if (remoteOrigin !== origin) {
                    log.info("The channel admin snippet seems to be loaded at different domain: (snippet, editor) => " + remoteOrigin + " != " + origin);
                }
            };

            // Received when manual publishing has updated the channel config from "publish" or from "channels_admin"
            // when it requests a re-rendering because of a user selection of a channel for a dom element with
            // multiple channels matching it...
            on[MessageTypes.POPULATE_CHANNELS] = (message) => {
                if (message.origin === "publish") {
                    if (initialized) {
                        this._refresh(
                            "Updated with manually saved configuration",
                            "Could not update with manually saved Slot configuration, please close the windows and try reopening them to continue Drag'n Drop Publishing!"
                        );
                    }

                // From "channels_admin"...
                } else {
                    this._postUpdateChannelsMessage();
                }
            };

            on[MessageTypes.PAGE_IS_CHANNELS_ENABLED] = (message) => {
                if (message.data.id === channelConfig.get("id")) {
                    // If already initialized, simply update the page status...
                    this._setPageStatus(message.data.status);
                } else {
                    this._setPageStatus(pageStatus.WRONG_ID);
                }
            };

            // Sets the highlight as dictated by the client site, e.g. disabled when a lightbox is opened...
            on[MessageTypes.SET_HIGHLIGHT] = (message) => {
                this._toggleHighlight(message.data);
            };

            on[MessageTypes.CONTENT]                    = this._resolveContent;
            on[MessageTypes.PUBLISH_TO_CHANNEL]         = this._publishToChannel;
            on[MessageTypes.CREATE_CHANNEL_AND_PUBLISH] = this._createChannelAndPublish;
            on[MessageTypes.DELETE_CHANNEL]             = this._deleteChannel;
            on[MessageTypes.REMOVE_SCHEDULED_CONTENT]   = this._removeContent;
            on[MessageTypes.REMOVE_CURRENT_CONTENT]     = this._removeContent;
            on[MessageTypes.SCHEDULED_DATE_CHANGED]     = this._scheduledDateChanged;

            node.on(on, this);

            // Send handshake now, since we are ready. The message will be enqueued until "channels_admin" joins
            // then dequeued and dispatched for it. However, if "channels_admin" leaves the node network and re-joins
            // later, we have to issue the handshake again (see above)...
            handshake();
        },

        /**
         * @private
         * Performs a refresh (fetch) of the channel config, as well as project when in project mode.
         *
         * @param {String} success The message to notify the user with on success; never falsey.
         * @param {String} error   The message to notify the user with on failure; never falsey.
         *
         * @return {Promise} A promise that will resolve once the refresh is done; never null.
         */
        _refresh(success, error) {
            const {channelConfig, project} = this.options;
            const promises = [channelConfig.fetch()];
            // Remember to reload project when in project mode, we might have add temp assets...
            if (project) {
                promises.push(project.fetch());
            }

            return this._updateAfterSync(Promise.all(promises), success, error);
        },

        onRender() {
            const {channelConfig, project} = this.options;

            this._setPageStatus(pageStatus.CHECKING);
            this._startTimer();
            this.stickit(channelConfig, this.channelBindings);

            this._saveButton = SequentialButton.add(this.ui.savePreviewButton, () => {
                const channelNames = pluck(channelConfig.get("channels"), "name");

                const eventProperties = {
                    channels: channelNames,
                    triggerOrigin: "SaveAndPublishButton",
                    dragAndDrop: true,
                    manual: false
                };

                EditorEvents.trigger(EditorEvents.constants.PUBLISH_CHANNEL, eventProperties);

                // When in project mode, we must ensure we update the project *after* channel save as the backend
                // will have added/tagged the assets correctly...
                const queue = SyncManager.queue(false); // false -> sequential queue!

                // Store a reference to the "previous" channels - `channels` will be overwritten on channelConfig save/sync.
                const previousChannels = channelConfig.get("channels");

                // First save...
                queue.enqueue("save", channelConfig);

                // Sync project, if any...
                if (project) {
                    queue.enqueue("fetch", project);
                    queue.enqueue({
                        method: "project.save",
                        fn() {
                            // Snapshop current persisted channels
                            const persistedChannels = new Map();

                            previousChannels.forEach((channel) => {
                                if (channel.id) {
                                    persistedChannels.set(channel.id, channel);
                                }
                            });

                            const assets = project.get("assets");

                            channelConfig.get("channels").forEach((channel) => {
                                if (!persistedChannels.has(channel.id)) {
                                    assets.addResource(channel.id, ProjectAsset.Type.CHANNEL);
                                }
                            });

                            return project.save();
                        }
                    });
                }

                // Start dequeing and update this view accordingly after completion...
                return this._updateAfterSync(
                    queue.sync(),
                    null,
                    "Could not save changes!",
                    true
                ) // true: notify PublishView that we have saved changes, and no longer tagged as changed
                    .done(() => {
                        // We need to generate this based on response data, so we cannot supply it up-front...
                        this._setMessage(SystemMessages.createPurgeMessage("Changes", "saved", channelConfig.get("purgeTime"), true), false, false, 8000);
                    });
            });

            // Set save button state initially...
            this._updateChangedButtons(changed);
        },

        _updateAfterSync(promise, success, error, updated) {
            return promise.done(() => {
                this._postUpdateChannelsMessage(updated); // "changed" will be set to !"updated"...

                if (success) {
                    this._setMessage(success);
                }

                changed = false; // never changed after sync!
                this._updateChangedButtons(changed);
            })
                .fail((err) => {
                    this._setMessage(error, err);
                    this._updateChangedButtons(changed);
                })
                .always(() => {
                // After sync, the client site will re-render, closing all opened lightboxes, so we must enable
                // the toggle highlight button if it was disabled explicitly because of this...
                    if (this._highlight === "disabled") {
                        this._toggleHighlight("restore");
                    }
                });
        },


        onShow() {
            const {channelConfig, project, publishableCollection} = this.options;
            const collectionView = new PublishToolbarThumbnailView({
                notAllowedToPublish:   this._notAllowedToPublish,
                channelConfig,
                project,
                filterDropdown:        true,
                showSortIcon:          false,
                collection:            publishableCollection,
                node
            });

            this.publishThumbnailRegion.parentView = this;
            this.publishThumbnailRegion.show(collectionView);
        },

        onClose() {
            if (node) {
                node.close();
                node = null;
            }

            this._clearTimer();
        },

        beforeNavigate() {
            return !closed && changed;
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/publish/PublishToolbarView.js